// بازگشت ورود داده (US-PLT-063): دکمه با ReasonDialog (عملیات برگشت‌ناپذیر — IA §4.4)؛ غیرفعال با توضیح وابستگی‌ها یا مهلت ۱۴ روزه
// از GET rollback-check (AC-PLT-063.2، BR-PLT-073).
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type ImportDependent, type ImportJobView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { JalaliDate, ReasonDialog } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { DependentLine, importKeys } from './common';

const ROLLBACKABLE = ['Completed', 'CompletedWithErrors', 'Failed'];

export function RollbackAction({ job, onRolledBack }: { job: ImportJobView; onRolledBack?: (job: ImportJobView) => void }) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.import.run');
  const idempotency = useIdempotencyKey();
  const [open, setOpen] = useState(false);
  const enabled = ROLLBACKABLE.includes(job.status);

  const check = useQuery({
    queryKey: [...importKeys.rollback(session?.tenantId, job.id), job.status],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/platform/imports/{id}/rollback-check', { params: { path: { id: job.id } } })),
    enabled,
  });

  const rollback = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/platform/imports/{id}:rollback', {
          params: { path: { id: job.id } },
          body: { reason },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (updated) => {
      setOpen(false);
      queryClient.setQueryData(importKeys.job(session?.tenantId, updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: importKeys.all(session?.tenantId) });
      onRolledBack?.(updated);
    },
    onError: () => void check.refetch(),
  });

  if (!enabled) {
    return null;
  }

  const blocked = check.data !== undefined && !check.data.canRollback;
  const dependents: ImportDependent[] =
    check.data?.dependents ??
    (isApiError(rollback.error) && Array.isArray(rollback.error.extensions.dependents)
      ? (rollback.error.extensions.dependents as ImportDependent[])
      : []);

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <span>
        <Button
          color="error"
          variant="outlined"
          startIcon={<UndoOutlinedIcon />}
          disabled={!write.canWrite || check.isPending || blocked}
          onClick={() => setOpen(true)}
          data-testid={`import-rollback-${job.id}`}
          aria-describedby={blocked ? `import-rollback-why-${job.id}` : undefined}
        >
          {t('plt:import.rollback.action')}
        </Button>
      </span>
      {blocked && check.data && (
        <Alert severity="info" id={`import-rollback-why-${job.id}`} data-testid="import-rollback-blocked">
          <Typography variant="body2">
            {t('plt:import.rollback.blocked')} {check.data.reasonKey ? t(`errors:${check.data.reasonKey}`) : null}
          </Typography>
          {dependents.length > 0 && (
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {dependents.map((dependent) => (
                <DependentLine key={dependent.kindKey} dependent={dependent} />
              ))}
            </ul>
          )}
        </Alert>
      )}
      {!blocked && check.data?.deadline && (
        <Typography variant="caption" color="text.secondary">
          {t('plt:import.rollback.deadline', { date: '' })}
          <JalaliDate value={check.data.deadline} withTime />
        </Typography>
      )}
      <ReasonDialog
        open={open}
        titleKey="plt:import.rollback.title"
        messageKey="plt:import.rollback.message"
        confirmKey="plt:import.rollback.action"
        busy={rollback.isPending}
        error={rollback.error}
        onCancel={() => setOpen(false)}
        onConfirm={(reason) => rollback.mutate(reason)}
      />
    </Stack>
  );
}
